import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { buildGrid, buildScene, FABRIC_GLSL, mat4Multiply, modelMatrix, perspective, sheetPoint, toScreen, } from './fabric-math';
/* ============================================================================
 * <FlowingFabric /> — the sculptural cloth behind the sign-in page.
 *
 * One WebGL2 draw per sheet (no library); lighting, the travelling blue light,
 * the cursor light and the selvedge lines are all computed per pixel. A single
 * rAF loop drives the GPU and moves any DOM anchors registered through
 * `useFabricAnchor`, so annotations ride the exact point of the cloth they
 * label. Without WebGL2 the same surface is drawn once as SVG threads.
 * ========================================================================== */
const VERT = /* glsl */ `#version 300 es
precision highp float;
in vec2 aUV;
uniform mat4 uModel;
uniform mat4 uProj;
${FABRIC_GLSL}
out vec3 vPos;
out vec3 vNormal;
out vec2 vUV;
out float vFold;
void main() {
  vec3 p = sheet(aUV);
  vFold = foldAt(aUV);
  vec3 pu = sheet(aUV + vec2(0.002, 0.0));
  vec3 pv = sheet(aUV + vec2(0.0, 0.004));
  vec4 w = uModel * vec4(p, 1.0);
  vPos = w.xyz;
  vNormal = mat3(uModel) * normalize(cross(pu - p, pv - p));
  vUV = aUV;
  gl_Position = uProj * w;
}`;
const FRAG = /* glsl */ `#version 300 es
precision highp float;
in vec3 vPos;
in vec3 vNormal;
in vec2 vUV;
in float vFold;
uniform vec4 uP2;
uniform vec4 uP3;
uniform float uTime;
uniform float uReveal;
uniform float uEdge;
uniform vec3 uLight;
uniform float uLightAmt;
out vec4 outColor;

const vec3 BLUE = vec3(0.192, 0.357, 1.0);   // #315BFF
const vec3 EBLUE = vec3(0.294, 0.435, 1.0);  // #4B6FFF
const vec3 TINT = vec3(0.933, 0.953, 1.0);   // #EEF3FF
const vec3 FOLD = vec3(0.77, 0.79, 0.84);
const vec3 PAPER = vec3(0.969, 0.973, 0.98); // #F7F8FA

void main() {
  float u = vUV.x;
  float v = vUV.y;

  // Unfurl from the u = 0 end on load.
  float front = uReveal * 1.32 - 0.16;
  float a = 1.0 - smoothstep(front - 0.14, front, u);
  a *= smoothstep(0.0, 0.03, u) * (1.0 - smoothstep(0.97, 1.0, u));
  if (a <= 0.002) discard;

  bool back = !gl_FrontFacing;
  vec3 V = normalize(-vPos);
  vec3 N = normalize(vNormal);
  // Shade whichever side faces the camera.
  if (dot(N, V) < 0.0) N = -N;
  // Raking key light from the upper left so the folds model clearly.
  vec3 L = normalize(vec3(-0.75, 0.62, 0.42));

  float shade = smoothstep(0.12, 0.95, dot(N, L) * 0.5 + 0.5);
  vec3 col = mix(FOLD, vec3(1.0), shade);
  // Valleys between folds hold a soft shadow.
  col *= 1.0 - 0.1 * smoothstep(0.0, -0.22, vFold);
  vec3 H = normalize(L + V);
  col += pow(max(dot(N, H), 0.0), 48.0) * 0.05;
  // The underside reads as a faint blue lining.
  if (back) col = mix(col, TINT * mix(0.86, 1.0, shade), 0.35);

  float fres = pow(1.0 - abs(dot(N, V)), 3.0);
  col = mix(col, EBLUE, fres * 0.14 * uEdge);

  // Light travelling along the folds.
  float head = fract(uTime * 0.035 + uP2.z * 0.37) * 1.7 - 0.35;
  float band = exp(-pow((u - head) * 6.5, 2.0));
  col = mix(col, EBLUE, band * (0.05 + 0.5 * fres) * uEdge);

  // Light following the cursor.
  vec2 dl = vPos.xy - uLight.xy;
  float glow = exp(-dot(dl, dl) * 2.4) * uLightAmt;
  col = mix(col, EBLUE, glow * (0.03 + 0.24 * fres));

  // Weave lines across the cloth; faded out before they can alias.
  if (uP3.y > 0.0) {
    float g = u * uP3.y;
    float fw = fwidth(g);
    float d = abs(fract(g - 0.5) - 0.5);
    float line = (1.0 - smoothstep(0.0, fw * 1.2, d)) * (1.0 - smoothstep(0.22, 0.45, fw));
    col = mix(col, mix(vec3(0.5, 0.55, 0.66), EBLUE, min(band + glow, 1.0)), line * (0.05 + 0.3 * band + 0.14 * glow));
  }

  // Selvedge: a crisp blue edge with a faint inward wash.
  float fe = fwidth(v);
  float edgeLine = 1.0 - smoothstep(fe * 0.6, fe * 2.0, 1.0 - abs(v));
  col = mix(col, EBLUE, smoothstep(0.8, 1.0, abs(v)) * 0.1 * uP3.w * uEdge);
  col = mix(col, BLUE, edgeLine * uP3.w * uEdge);

  // Unfurling front catches the light.
  col = mix(col, EBLUE, exp(-pow((u - front + 0.07) * 18.0, 2.0)) * 0.45 * (1.0 - uReveal));

  // Atmospheric depth: far folds sink toward the paper.
  col = mix(col, PAPER, smoothstep(-4.4, -6.4, vPos.z) * 0.6);

  float alpha = a * uP3.z;
  outColor = vec4(col * alpha, alpha);
}`;
const FabricContext = createContext(null);
/** Runs `fn` on every fabric frame (after the GPU draw). */
export function useFabricFrame(fn) {
    const api = useContext(FabricContext);
    const ref = useRef(fn);
    ref.current = fn;
    useEffect(() => api === null || api === void 0 ? void 0 : api.subscribe((f) => ref.current(f)), [api]);
}
/**
 * Pins an element to (u, v) on a sheet. With `react`, the element also
 * swells and brightens as the pointer comes near.
 */
export function useFabricAnchor(sheet, u, v, react = false) {
    const ref = useRef(null);
    useFabricFrame((f) => {
        const el = ref.current;
        if (!el)
            return;
        const [x, y] = f.project(sheet, u, v);
        let s = 1;
        if (react && f.pointer) {
            const d = Math.hypot(f.pointer[0] - x, f.pointer[1] - y);
            const near = Math.max(0, 1 - d / 180);
            s = 1 + near * 1.6;
            el.style.opacity = String(0.55 + near * 0.45);
        }
        el.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)${s !== 1 ? ` scale(${s.toFixed(3)})` : ''}`;
    });
    return ref;
}
function compile(gl, type, src) {
    const sh = gl.createShader(type);
    if (!sh)
        return null;
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        gl.deleteShader(sh);
        return null;
    }
    return sh;
}
function initGL(canvas) {
    const gl = canvas.getContext('webgl2', {
        alpha: true,
        premultipliedAlpha: true,
        antialias: true,
        depth: true,
        powerPreference: 'low-power',
        // Software-rendered GL would cost more than it is worth: use the SVG cloth.
        failIfMajorPerformanceCaveat: true,
    });
    if (!gl)
        return null;
    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    const program = gl.createProgram();
    if (!vs || !fs || !program)
        return null;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS))
        return null;
    const vao = gl.createVertexArray();
    if (!vao)
        return null;
    const names = [
        'uModel',
        'uProj',
        'uP0',
        'uP1',
        'uP2',
        'uP3',
        'uTime',
        'uReveal',
        'uEdge',
        'uLight',
        'uLightAmt',
    ];
    const loc = {};
    names.forEach((n) => {
        loc[n] = gl.getUniformLocation(program, n);
    });
    gl.enable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    return { gl, program, vao, count: 0, segments: '', loc };
}
function uploadGrid(s, su, sv) {
    const key = `${su}x${sv}`;
    if (s.segments === key)
        return;
    const { gl, program, vao } = s;
    const { uv, idx } = buildGrid(su, sv);
    gl.bindVertexArray(vao);
    const vb = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, vb);
    gl.bufferData(gl.ARRAY_BUFFER, uv, gl.STATIC_DRAW);
    const aUV = gl.getAttribLocation(program, 'aUV');
    gl.enableVertexAttribArray(aUV);
    gl.vertexAttribPointer(aUV, 2, gl.FLOAT, false, 0, 0);
    const ib = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, idx, gl.STATIC_DRAW);
    s.count = idx.length;
    s.segments = key;
}
function draw(s, scene, model, proj, time, reveal, edge, light, lightAmt) {
    const { gl, loc } = s;
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.useProgram(s.program);
    gl.bindVertexArray(s.vao);
    gl.uniformMatrix4fv(loc.uModel, false, model);
    gl.uniformMatrix4fv(loc.uProj, false, proj);
    gl.uniform1f(loc.uTime, time);
    gl.uniform1f(loc.uReveal, reveal);
    gl.uniform1f(loc.uEdge, edge);
    gl.uniform3f(loc.uLight, light[0], light[1], light[2]);
    gl.uniform1f(loc.uLightAmt, lightAmt);
    // Opaque sheets write depth; translucent ones are layered on top of them.
    const order = scene.sheets
        .map((sh, i) => ({ sh, i }))
        .sort((a, b) => Number(a.sh.opacity < 1) - Number(b.sh.opacity < 1));
    order.forEach(({ sh }) => {
        gl.depthMask(sh.opacity >= 1);
        gl.uniform4f(loc.uP0, sh.x0, sh.x1, sh.y0, sh.y1);
        gl.uniform4f(loc.uP1, sh.bow, sh.zAmp, sh.width, sh.twist0);
        gl.uniform4f(loc.uP2, sh.twist1, sh.fold, sh.phase, sh.speed);
        gl.uniform4f(loc.uP3, sh.zBase, sh.lines, sh.opacity, sh.edge);
        gl.drawElements(gl.TRIANGLES, s.count, gl.UNSIGNED_SHORT, 0);
    });
    gl.depthMask(true);
}
/* ---------------------------- SVG fallback ------------------------------- */
function FallbackThreads({ scene, model, proj, width, height, }) {
    const vp = useMemo(() => mat4Multiply(proj, model), [model, proj]);
    const paths = useMemo(() => {
        const out = [];
        scene.sheets.forEach((sh, si) => {
            const threads = sh.width > 0.2 ? 22 : 3;
            for (let j = 0; j <= threads; j += 1) {
                const v = (j / threads) * 2 - 1;
                let d = '';
                for (let i = 0; i <= 90; i += 1) {
                    const [x, y] = toScreen(vp, sheetPoint(sh, i / 90, v, 12), width, height);
                    d += `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`;
                }
                out.push({ d, edge: j === 0 || j === threads, key: `${si}-${j}` });
            }
        });
        return out;
    }, [scene, vp, width, height]);
    return (_jsx("svg", { className: 'vx-fabric__fallback', viewBox: `0 0 ${width} ${height}`, "aria-hidden": 'true', children: paths.map((p) => (_jsx("path", { d: p.d, fill: 'none', stroke: p.edge ? '#4B6FFF' : '#0B1020', strokeOpacity: p.edge ? 0.7 : 0.1, strokeWidth: p.edge ? 1.25 : 1 }, p.key))) }));
}
export function FlowingFabric({ variant, reserveRight = 0, pointer, reduced, delay = 0.3, className, children, }) {
    const wrapRef = useRef(null);
    const canvasRef = useRef(null);
    const listeners = useRef(new Set());
    const [fallback, setFallback] = useState(null);
    const subscribe = useCallback((fn) => {
        listeners.current.add(fn);
        return () => {
            listeners.current.delete(fn);
        };
    }, []);
    const api = useMemo(() => ({ subscribe }), [subscribe]);
    useEffect(() => {
        const wrap = wrapRef.current;
        const canvas = canvasRef.current;
        if (!wrap || !canvas)
            return undefined;
        let gl = initGL(canvas);
        let raf = 0;
        let visible = true;
        let width = 0;
        let height = 0;
        let left = 0;
        let top = 0;
        let drewFallback = false;
        // Adaptive quality: drop resolution if frames run long on a weak GPU.
        let dprCap = variant === 'full' ? 1.75 : 2;
        let last = 0;
        let slow = 0;
        let sampled = 0;
        let scene = null;
        let proj = perspective(1);
        const start = performance.now();
        const ease = (x) => 1 - Math.pow(1 - Math.min(Math.max(x, 0), 1), 4);
        const frame = (now) => {
            raf = 0;
            if (!scene || !width)
                return;
            if (last && gl && dprCap > 1) {
                sampled += 1;
                if (now - last > 26)
                    slow += 1;
                if (sampled >= 90) {
                    if (slow > 45) {
                        dprCap = 1;
                        resize();
                    }
                    sampled = 0;
                    slow = 0;
                }
            }
            last = now;
            const elapsed = (now - start) / 1000;
            const still = reduced || !gl;
            const time = still ? 14 : elapsed + 6;
            const reveal = still ? 1 : ease((elapsed - delay) / 2.6);
            const edge = still ? 1 : ease((elapsed - delay - 0.2) / 1.6);
            const active = still ? 0 : pointer.active.get();
            const mx = still ? 0 : pointer.x.get();
            const my = still ? 0 : pointer.y.get();
            const drift = still ? 0 : Math.sin(elapsed * 0.09) * 0.018;
            const model = modelMatrix(-mx * 0.06, my * 0.04, my * 0.05 + 0.02, mx * 0.075, drift);
            // Without a fine pointer the light wanders on its own.
            const lx = active
                ? mx * scene.halfW
                : scene.cx + Math.sin(elapsed * 0.17) * scene.halfW * 0.5;
            const ly = active ? -my * scene.halfH : Math.cos(elapsed * 0.13) * scene.halfH * 0.5;
            if (gl) {
                draw(gl, scene, model, proj, time, reveal, edge, [lx, ly, -4], still ? 0 : 1);
            }
            else if (!drewFallback) {
                drewFallback = true;
                setFallback({ scene, model, proj, width, height });
            }
            const vp = mat4Multiply(proj, model);
            const sheets = scene.sheets;
            const f = {
                time,
                width,
                height,
                pointer: active
                    ? [
                        (mx * 0.5 + 0.5) * window.innerWidth - left,
                        (my * 0.5 + 0.5) * window.innerHeight - top,
                    ]
                    : null,
                project: (si, u, v) => toScreen(vp, sheetPoint(sheets[Math.min(si, sheets.length - 1)], u, v, time), width, height),
            };
            listeners.current.forEach((fn) => fn(f));
            if (!still && visible)
                raf = requestAnimationFrame(frame);
        };
        const kick = () => {
            last = 0;
            if (!raf)
                raf = requestAnimationFrame(frame);
        };
        const measure = () => {
            const rect = wrap.getBoundingClientRect();
            left = rect.left;
            top = rect.top;
            return rect;
        };
        const resize = () => {
            const rect = measure();
            width = Math.max(1, Math.round(rect.width));
            height = Math.max(1, Math.round(rect.height));
            const dpr = Math.min(window.devicePixelRatio || 1, dprCap);
            canvas.width = Math.round(width * dpr);
            canvas.height = Math.round(height * dpr);
            const span = Math.max(0.4, (width - reserveRight) / width);
            scene = buildScene(width, height, variant, span / 2, span);
            proj = perspective(width / height);
            if (gl) {
                gl.gl.viewport(0, 0, canvas.width, canvas.height);
                uploadGrid(gl, scene.segments[0], scene.segments[1]);
            }
            else {
                drewFallback = false;
            }
            kick();
        };
        const ro = new ResizeObserver(resize);
        ro.observe(wrap);
        resize();
        // Stop drawing while the stage is scrolled out of view.
        const io = new IntersectionObserver(([entry]) => {
            visible = entry.isIntersecting;
            if (visible)
                kick();
        });
        io.observe(wrap);
        const onLost = (e) => {
            e.preventDefault();
            gl = null;
            cancelAnimationFrame(raf);
            raf = 0;
            kick();
        };
        const onRestored = () => {
            gl = initGL(canvas);
            if (gl && scene) {
                gl.gl.viewport(0, 0, canvas.width, canvas.height);
                uploadGrid(gl, scene.segments[0], scene.segments[1]);
            }
            setFallback(null);
            kick();
        };
        canvas.addEventListener('webglcontextlost', onLost);
        canvas.addEventListener('webglcontextrestored', onRestored);
        window.addEventListener('scroll', measure, { passive: true });
        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
            io.disconnect();
            canvas.removeEventListener('webglcontextlost', onLost);
            canvas.removeEventListener('webglcontextrestored', onRestored);
            window.removeEventListener('scroll', measure);
        };
    }, [variant, reserveRight, reduced, delay, pointer]);
    return (_jsx(FabricContext.Provider, { value: api, children: _jsxs("div", { ref: wrapRef, className: `vx-fabric${className ? ` ${className}` : ''}`, children: [_jsx("canvas", { ref: canvasRef, className: 'vx-fabric__canvas', "aria-hidden": 'true' }), fallback && _jsx(FallbackThreads, Object.assign({}, fallback)), _jsx("div", { className: 'vx-fabric__overlay', children: children })] }) }));
}
